import React, { memo, useMemo, useCallback } from 'react';

import { cn } from '@/lib/utils';
import type {
  IPurchaseRequestItemBody,
  ISuppliarWithDebt,
} from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';

interface RequestFooterProps {
  requestItems: IPurchaseRequestItemBody[] | null;
  suppliarWithDebt?: ISuppliarWithDebt | null;
  notes: string;
  setNotes: (value: string) => void;
  className?: string;
}

// Memoized formatNumber function to prevent recreating on every render
const formatNumber = (num: number) => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Memoized FooterItem component
const FooterItem = memo(
  ({
    label,
    value,
    className,
    valueClassName,
  }: {
    label: string;
    value: string;
    className?: string;
    valueClassName?: string;
  }) => {
    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-lg border shadow-sm transition-all duration-200 hover:shadow-md',
          className,
        )}
      >
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_80%_60%,rgba(255,255,255,0.1),transparent_70%)]"></div>
        <div className="flex flex-1 items-center justify-center bg-gradient-to-b from-white to-gray-50/80 px-3 py-2.5 text-center dark:from-slate-800 dark:to-slate-900/90">
          <span
            className={cn(
              'font-mono text-sm font-bold tracking-tight',
              valueClassName,
            )}
          >
            {value}
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center justify-center border-r bg-gradient-to-b from-gray-50 to-gray-100 px-3 py-1 text-slate-600 dark:from-slate-700 dark:to-slate-800 dark:text-slate-300">
          <span className="text-xs font-medium">{label}</span>
        </div>
      </div>
    );
  },
);
FooterItem.displayName = 'FooterItem';

// Memoized SupplierDebtDisplay component
const SupplierDebtDisplay = memo(
  ({ suppliarWithDebt }: { suppliarWithDebt: ISuppliarWithDebt }) => {
    // Determine the display text and style based on debt
    const debtText = useMemo(() => {
      if (suppliarWithDebt.Debt > 0) {
        return `دائن ${formatNumber(Math.abs(suppliarWithDebt.Debt))}`;
      } else if (suppliarWithDebt.Debt < 0) {
        return `مديون ${formatNumber(Math.abs(suppliarWithDebt.Debt))}`;
      } else {
        return 'خالص';
      }
    }, [suppliarWithDebt.Debt]);

    // Determine the class based on the debt type
    const debtClass = useMemo(() => {
      if (suppliarWithDebt.Debt > 0) {
        return 'border-green-300/70 bg-gradient-to-br from-green-50 to-green-100/80 text-green-800 dark:border-green-800/50 dark:from-green-900/30 dark:to-green-800/20 dark:text-green-300';
      } else if (suppliarWithDebt.Debt < 0) {
        return 'border-red-300/70 bg-gradient-to-br from-red-50 to-red-100/80 text-red-800 dark:border-red-800/50 dark:from-red-900/30 dark:to-red-800/20 dark:text-red-300';
      } else {
        return 'border-slate-300/70 bg-gradient-to-br from-slate-50 to-slate-100/80 text-slate-800 dark:border-slate-700/50 dark:from-slate-800/60 dark:to-slate-900/30 dark:text-slate-300';
      }
    }, [suppliarWithDebt.Debt]);

    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-lg border p-2.5 text-center shadow-sm transition-all duration-200 hover:shadow-md',
          debtClass,
        )}
      >
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.15),transparent_70%)]"></div>
        <div className="mb-1.5 text-xs font-medium tracking-wide opacity-80">
          حساب سابق
        </div>
        <div className="font-mono text-xl font-bold">{debtText}</div>
      </div>
    );
  },
);
SupplierDebtDisplay.displayName = 'SupplierDebtDisplay';

// Memoized TotalAccountDisplay component
const TotalAccountDisplay = memo(
  ({
    suppliarWithDebt,
    totalAfterDiscount,
  }: {
    suppliarWithDebt: ISuppliarWithDebt;
    totalAfterDiscount: number;
  }) => {
    // Calculate the new debt
    const newDebt = useMemo(
      () => suppliarWithDebt.Debt + totalAfterDiscount,
      [suppliarWithDebt.Debt, totalAfterDiscount],
    );

    // Generate the status text
    const statusText = useMemo(() => {
      if (newDebt > 0) {
        return `دائن ${formatNumber(Math.abs(newDebt))}`;
      } else if (newDebt < 0) {
        return `مديون ${formatNumber(Math.abs(newDebt))}`;
      } else {
        return 'خالص';
      }
    }, [newDebt]);

    // Generate the status class
    const statusClass = useMemo(() => {
      if (newDebt > 0) {
        return 'border-green-400/60 bg-gradient-to-br from-green-50 to-green-100/80 text-green-800 dark:border-green-700/50 dark:from-green-900/30 dark:to-green-800/20 dark:text-green-300';
      } else if (newDebt < 0) {
        return 'border-red-400/60 bg-gradient-to-br from-red-50 to-red-100/80 text-red-800 dark:border-red-700/50 dark:from-red-900/30 dark:to-red-800/20 dark:text-red-300';
      } else {
        return 'border-slate-300/70 bg-gradient-to-br from-slate-50 to-slate-100/80 text-slate-800 dark:border-slate-700/50 dark:from-slate-800/60 dark:to-slate-900/30 dark:text-slate-300';
      }
    }, [newDebt]);

    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-lg border-2 p-3 text-center shadow-md transition-all duration-200 hover:shadow-lg',
          statusClass,
        )}
      >
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.2),transparent_70%)]"></div>
        <div className="flex flex-col">
          <div className="mb-1.5 text-xs font-medium tracking-wide opacity-80">
            حساب إجمالي
          </div>
          <div className="font-mono text-2xl font-bold">{statusText}</div>
          <div className="mt-2 flex items-center justify-center gap-2 text-center text-xs font-medium">
            <span className="rounded-md bg-white/30 px-2 py-0.5 dark:bg-black/20">
              {formatNumber(suppliarWithDebt.Debt)}
            </span>
            <span className="text-opacity-60">+</span>
            <span className="rounded-md bg-white/30 px-2 py-0.5 dark:bg-black/20">
              {formatNumber(totalAfterDiscount)}
            </span>
          </div>
        </div>
      </div>
    );
  },
);
TotalAccountDisplay.displayName = 'TotalAccountDisplay';

// Memoized NotesInput component
const NotesInput = memo(
  ({
    notes,
    setNotes,
  }: {
    notes: string;
    setNotes: (value: string) => void;
  }) => {
    const handleNotesChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.target.value.slice(0, 200));
      },
      [setNotes],
    );

    return (
      <div className="relative">
        <textarea
          id="notes"
          placeholder="أضف ملاحظاتك هنا..."
          className="min-h-[100px] w-full resize-none rounded-lg border border-input bg-background/80 px-3 py-2.5 text-sm shadow-sm ring-offset-background backdrop-blur-sm transition-colors placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          value={notes}
          onChange={handleNotesChange}
          maxLength={200}
          dir="rtl"
        />
        <div className="absolute bottom-2 left-2 text-xs text-slate-400 dark:text-slate-500">
          {notes.length}/200
        </div>
      </div>
    );
  },
);
NotesInput.displayName = 'NotesInput';

// Memoized main component
const RequestFooter = memo(
  ({
    requestItems,
    suppliarWithDebt,
    notes,
    setNotes,
    className,
  }: RequestFooterProps) => {
    // Calculate all totals in a single pass for better performance
    const totals = useMemo(() => {
      if (!requestItems || requestItems.length === 0) {
        return null;
      }

      const itemCount = requestItems.length;
      let totalQty = 0;
      let totalBonus = 0;
      let totalPrice = 0;
      let totalCustomerPrice = 0;
      let totalTax = 0;
      let totalAfterDiscount = 0;

      // Single loop calculation for better performance
      for (const item of requestItems) {
        totalQty += item.Qty || 0;
        totalBonus += item.Bonus || 0;
        totalPrice += item.Price * item.Qty || 0;
        totalCustomerPrice += item.TotalCustomerPrice || 0;
        totalTax += item.TotalTax || 0;
        totalAfterDiscount += item.TotalAfterDiscount || 0;
      }

      // Calculate discount information
      const totalDiscountAmount = totalPrice - totalAfterDiscount;
      const averageDiscount =
        totalPrice > 0 ? (totalDiscountAmount / totalPrice) * 100 : 0;

      return {
        itemCount,
        totalQty,
        totalBonus,
        totalPrice,
        totalCustomerPrice,
        totalTax,
        totalAfterDiscount,
        totalDiscountAmount,
        averageDiscount,
      };
    }, [requestItems]);

    // Early return if no items or if calculations yielded null
    if (!totals) {
      return null;
    }

    // Determine if requestItems exists and has items
    const hasItems = requestItems && requestItems.length > 0;

    return (
      <div
        className={cn(
          'w-full overflow-hidden rounded-xl border bg-gradient-to-br from-white via-slate-50/80 to-slate-100/60 p-3 shadow-md backdrop-blur-sm transition-all duration-300 dark:border-gray-700/60 dark:from-gray-900 dark:via-gray-900/80 dark:to-gray-950/70',
          className,
        )}
      >
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_80%_0%,rgba(238,238,238,0.15),transparent_50%)] dark:bg-[radial-gradient(circle_at_80%_0%,rgba(56,56,56,0.15),transparent_50%)]"></div>

        {/* Line 1: All metrics in a single row */}
        <div className="grid grid-cols-7 gap-2">
          <div className="col-span-1">
            <FooterItem
              label="الأصناف"
              value={totals.itemCount.toString()}
              className="bg-gradient-to-br from-blue-50 via-blue-50/90 to-blue-100/80 text-blue-800 dark:from-blue-900/40 dark:via-blue-900/30 dark:to-blue-800/20 dark:text-blue-300"
            />
          </div>
          <div className="col-span-1">
            <FooterItem
              label="الكمية"
              value={`${formatNumber(totals.totalQty)}`}
              className="bg-gradient-to-br from-cyan-50 via-cyan-50/90 to-cyan-100/80 text-cyan-800 dark:from-cyan-900/40 dark:via-cyan-900/30 dark:to-cyan-800/20 dark:text-cyan-300"
            />
          </div>
          <div className="col-span-1">
            <FooterItem
              label="الضريبة"
              value={formatNumber(totals.totalTax)}
              className="bg-gradient-to-br from-indigo-50 via-indigo-50/90 to-indigo-100/80 text-indigo-800 dark:from-indigo-900/40 dark:via-indigo-900/30 dark:to-indigo-800/20 dark:text-indigo-300"
            />
          </div>
          <div className="col-span-1">
            <FooterItem
              label="الخصم"
              value={`${totals.averageDiscount.toFixed(1)}%`}
              className="bg-gradient-to-br from-amber-50 via-amber-50/90 to-amber-100/80 text-amber-800 dark:from-amber-900/40 dark:via-amber-900/30 dark:to-amber-800/20 dark:text-amber-300"
            />
          </div>
          <div className="col-span-1">
            <FooterItem
              label="الجمهور"
              value={formatNumber(totals.totalCustomerPrice)}
              className="bg-gradient-to-br from-rose-50 via-rose-50/90 to-rose-100/80 text-rose-800 dark:from-rose-900/40 dark:via-rose-900/30 dark:to-rose-800/20 dark:text-rose-300"
            />
          </div>
          <div className="col-span-2">
            <FooterItem
              label="الطلبية"
              value={`+ ${formatNumber(totals.totalAfterDiscount)}`}
              className="relative overflow-hidden border-2 border-emerald-300/70 bg-gradient-to-br from-emerald-50 via-emerald-50/90 to-emerald-100/80 font-extrabold text-emerald-800 shadow-emerald-200/20 dark:border-emerald-800/40 dark:from-emerald-900/40 dark:via-emerald-900/30 dark:to-emerald-800/20 dark:text-emerald-300 dark:shadow-emerald-900/10"
              valueClassName="text-base font-mono"
            />
          </div>
        </div>

        {/* Line 2: Notes and account information in a single row */}
        <div className="mt-2 grid grid-cols-7 gap-2">
          <div className="col-span-3">
            <div className="relative h-full">
              <textarea
                id="notes"
                placeholder="أضف ملاحظاتك هنا..."
                className="h-full min-h-[40px] w-full resize-none rounded-lg border border-input bg-background/80 px-3 py-2 text-sm shadow-sm ring-offset-background backdrop-blur-sm transition-colors placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                maxLength={200}
                dir="rtl"
              />
              <div className="absolute bottom-1 left-2 text-[10px] text-slate-400 dark:text-slate-500">
                {notes.length}/200
              </div>
            </div>
          </div>

          {suppliarWithDebt && (
            <div className="col-span-2">
              <div
                className={cn(
                  'relative h-full overflow-hidden rounded-lg border p-2 text-center shadow-sm transition-all duration-200 hover:shadow-md',
                  suppliarWithDebt.Debt > 0
                    ? 'border-green-300/70 bg-gradient-to-br from-green-50 to-green-100/80 text-green-800 dark:border-green-800/50 dark:from-green-900/30 dark:to-green-800/20 dark:text-green-300'
                    : suppliarWithDebt.Debt < 0
                      ? 'border-red-300/70 bg-gradient-to-br from-red-50 to-red-100/80 text-red-800 dark:border-red-800/50 dark:from-red-900/30 dark:to-red-800/20 dark:text-red-300'
                      : 'border-slate-300/70 bg-gradient-to-br from-slate-50 to-slate-100/80 text-slate-800 dark:border-slate-700/50 dark:from-slate-800/60 dark:to-slate-900/30 dark:text-slate-300',
                )}
              >
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.15),transparent_70%)]"></div>
                <div className="mb-0.5 text-[10px] font-medium tracking-wide opacity-80">
                  حساب سابق
                </div>
                <div className="font-mono text-sm font-bold">
                  {suppliarWithDebt.Debt > 0
                    ? `دائن ${formatNumber(Math.abs(suppliarWithDebt.Debt))}`
                    : suppliarWithDebt.Debt < 0
                      ? `مديون ${formatNumber(Math.abs(suppliarWithDebt.Debt))}`
                      : 'خالص'}
                </div>
              </div>
            </div>
          )}

          {suppliarWithDebt && hasItems && (
            <div className="col-span-2">
              <div
                className={cn(
                  'relative h-full overflow-hidden rounded-lg border-2 p-2 text-center shadow-md transition-all duration-200 hover:shadow-lg',
                  suppliarWithDebt.Debt + totals.totalAfterDiscount > 0
                    ? 'border-green-400/60 bg-gradient-to-br from-green-50 to-green-100/80 text-green-800 dark:border-green-700/50 dark:from-green-900/30 dark:to-green-800/20 dark:text-green-300'
                    : suppliarWithDebt.Debt + totals.totalAfterDiscount < 0
                      ? 'border-red-400/60 bg-gradient-to-br from-red-50 to-red-100/80 text-red-800 dark:border-red-700/50 dark:from-red-900/30 dark:to-red-800/20 dark:text-red-300'
                      : 'border-slate-300/70 bg-gradient-to-br from-slate-50 to-slate-100/80 text-slate-800 dark:border-slate-700/50 dark:from-slate-800/60 dark:to-slate-900/30 dark:text-slate-300',
                )}
              >
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.2),transparent_70%)]"></div>
                <div className="mb-0.5 text-[10px] font-medium tracking-wide opacity-80">
                  حساب إجمالي
                </div>
                <div className="font-mono text-sm font-bold">
                  {suppliarWithDebt.Debt + totals.totalAfterDiscount > 0
                    ? `دائن ${formatNumber(Math.abs(suppliarWithDebt.Debt + totals.totalAfterDiscount))}`
                    : suppliarWithDebt.Debt + totals.totalAfterDiscount < 0
                      ? `مديون ${formatNumber(Math.abs(suppliarWithDebt.Debt + totals.totalAfterDiscount))}`
                      : 'خالص'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

RequestFooter.displayName = 'RequestFooter';

export default RequestFooter;
